"use client";

import { useState, useCallback, useEffect } from "react";
import { Input } from "src/components/ui/input";
import { Card, CardContent } from "src/components/ui/card";
import { Button } from "src/components/ui/button";
import { Loader2, Save } from "lucide-react";
import { DatePicker, TimePicker } from "antd";
import { QuestionType } from "src/lib/types";
import { generateId } from "src/lib/utils";
import dayjs from "dayjs";
import { useLanguage } from "../../../../../context/LanguageContext";
import EditorTextArea from "../../../../../components/EditorTextArea";
import { toast } from "sonner";
import QuizBuilder from "../../../../../components/Quiz/quiz-builder";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { SubLessonData } from "../../../../../lib/languages";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../../components/ui/select";
import { useAPI } from "../../../../../context/APIContext";
import { BaseUrlApi, ErrorMessage } from "../../../../../lib/api";

const { RangePicker } = DatePicker;

export default function AddQuiz() {
  const navigate = useNavigate();

  const { id } = useParams();
  const { state } = useLocation();
  const isTeacher = state?.isTeacher;

  // Get All Lessons
  // const { lessonsItems, coursesItems, categoriesItems } = useAPI();
  // const { lessons, loadingLessons } = lessonsItems;
  // const { categories, loadingCategories } = categoriesItems;
  // const { loadingCourses, courses } = coursesItems;

  // Get All Lessons
  const [loadingLessons, setLoadingLessons] = useState(true);
  const [lessons, setLessons] = useState([]);
  const AllLessons = () => {
    axios
      .get(`${BaseUrlApi}/teacher/lessons`)
      .then(({ data }) => {
        setLessons(data.data);
      })
      .catch((error) => {
        toast("Error loading lessons", {
          description: ErrorMessage(error),
        });
      })
      .finally(() => setLoadingLessons(false));
  };

  useEffect(() => {
    AllLessons();
  }, []);

  // Use the useLanguage hook; if not available, fallback to English.
  const languageContext = { language: "en" };
  const { language } = useLanguage?.() || languageContext;
  const t = SubLessonData[language] || SubLessonData["en"];

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Initialize form with all required fields
  const [form, setForm] = useState({
    title_en: "",
    title_ar: "",
    description_en: "",
    description_ar: "",
    duration: "01:00:00",
    start_date: null,
    end_date: null,
    show_degree: 0,
    show_answer: 0,
    questions: [
      {
        id: generateId(),
        type: QuestionType.MULTIPLE_CHOICE,
        text: "",
        score: "5.00",
        options: [
          { id: generateId(), text: "", isCorrect: false },
          { id: generateId(), text: "", isCorrect: false },
        ],
        file: null,
      },
    ],
    lesson_id: "",
    course_id: "",
    category_id: "",
  });

  // Handle duration change
  const handleDurationChange = (time, timeString) => {
    setForm({ ...form, duration: timeString });
  };

  // Handle date range change
  const handleDateRangeChange = (dates, dateStrings) => {
    if (dates) {
      setForm({
        ...form,
        start_date: dateStrings[0],
        end_date: dateStrings[1],
      });
    } else {
      setForm({
        ...form,
        start_date: null,
        end_date: null,
      });
    }
  };

  // Update questions from quiz builder – memoized to prevent infinite loops
  const updateQuestions = useCallback((questions) => {
    const formattedQuestions = questions.map((question) => ({
      text: question.text,
      type:
        question.type === QuestionType.MULTIPLE_CHOICE
          ? "mcq"
          : question.type === QuestionType.TRUE_FALSE
          ? "true_false"
          : question.type === QuestionType.PARAGRAPH
          ? "written"
          : "file_upload",
      score: question.score || "5.00",
      file: question.imageUrl,
      options: question.options.map((option) => ({
        text: option.text,
        isCorrect: option.isCorrect ? 1 : 0,
        image: null,
      })),
    }));

    setForm((prevForm) => ({ ...prevForm, questions: formattedQuestions }));
  }, []);

  // Handle form submission
  const handleSubmitQuiz = async () => {
    setLoading(true);
    setErrors({});

    // Validate form
    const newErrors = {};
    if (!form.title_en && !form.title_ar)
      newErrors.title = ["Title is required"];
    if (!form.duration) newErrors.duration = ["Duration is required"];
    if (!form.start_date || !form.end_date)
      newErrors.dates = ["Start and end dates are required"];
    if (form.questions.length === 0)
      newErrors.questions = ["At least one question is required"];

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setLoading(false);
      toast("Validation Error", {
        description: "Please check the form for errors",
      });
      return;
    }

    try {
      const formData = new FormData();

      formData.append(
        "title",
        JSON.stringify({
          en: form.title_en,
          ar: form.title_ar,
        })
      );

      formData.append(
        "description",
        JSON.stringify({
          en: form.description_en,
          ar: form.description_ar,
        })
      );

      formData.append("duration", form.duration);
      formData.append("start_date", form.start_date);
      formData.append("end_date", form.end_date);
      formData.append("show_answer", form.show_answer);
      formData.append("show_degree", form.show_degree);
      formData.append("lesson_id", isTeacher ? form.lesson_id : id);

      form.questions.forEach((question, i) => {
        formData.append(`question[${i}][text]`, question.text || "");
        formData.append(`question[${i}][type]`, question.type || "");
        formData.append(`question[${i}][file]`, question.file || "");
        formData.append(`question[${i}][score]`, question.score || "5.00");

        question.options.forEach((option, j) => {
          formData.append(`question[${i}][options][${j}][text]`, option.text);
          formData.append(
            `question[${i}][options][${j}][isCorrect]`,
            option.isCorrect ? "1" : "0"
          );
        });
      });

      let APiUrl;
      if (!!isTeacher) {
        APiUrl = form.lesson_id
          ? `lesson`
          : form.course_id
          ? `course/${form.course_id}`
          : form.category_id && `category/${form.category_id}`;
      }

      await axios.post(
        `https://pro1.edunezam.com/api/quizzes/${
          isTeacher ? APiUrl : `lesson/${id}`
        }`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      toast("Success", {
        description: "Quiz created successfully",
      });

      navigate(-1);
    } catch (error) {
      console.error("Error creating quiz:", error);
      toast("Error", {
        description: "Failed to create quiz. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-10" dir={language === "ar" ? "rtl" : "ltr"}>
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="w-full sm:w-fit">
          <p className="font-bold text-2xl pt-5" id="category">
            {t.addQuizTitle}
          </p>
          <p className="font-medium text-sm text-muted-foreground">
            {t.addQuizDescription}
          </p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-fit">
          <Button
            className="w-full sm:w-fit h-10 bg-blue-600 text-white hover:bg-blue-600/80 rounded-xl"
            onClick={handleSubmitQuiz}
            disabled={loading}
          >
            {loading ? (
              <div className="animate-spin mr-2">
                <Loader2 className="h-4 w-4" />
              </div>
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {t.submitQuiz}
          </Button>
        </div>
      </div>

      <div className="col-span-1 lg:col-span-2">
        {/* General information */}
        <Card className="h-full flex flex-col">
          <CardContent className="p-4 space-y-4 flex-1 flex flex-col">
            <p className="font-bold text-xl border-b pb-2">
              {t.generalInformation}
            </p>
            <div className="space-y-4 flex-1 flex flex-col">
              {/* Title */}
              <div className="flex items-center gap-4 flex-col sm:flex-row">
                <div className="flex flex-col gap-2 w-full">
                  <label htmlFor="title_en" className="text-sm">
                    {t.titleEn}
                  </label>
                  <Input
                    readOnly={loading}
                    id="title_en"
                    name="title_en"
                    type="text"
                    placeholder={t.enterTitleEn}
                    value={form.title_en}
                    onChange={(e) =>
                      setForm({ ...form, title_en: e.target.value })
                    }
                  />
                  {errors?.title && (
                    <p className="text-red-500 font-semibold text-sm">
                      {errors.title[0]}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-2 w-full">
                  <label htmlFor="title_ar" className="text-sm">
                    {t.titleAr}
                  </label>
                  <Input
                    readOnly={loading}
                    id="title_ar"
                    name="title_ar"
                    type="text"
                    placeholder={t.enterTitleAr}
                    value={form.title_ar}
                    onChange={(e) =>
                      setForm({ ...form, title_ar: e.target.value })
                    }
                  />
                </div>
              </div>
              {/* Description */}
              <div className="flex flex-col gap-2 flex-1">
                <label htmlFor="description" className="text-sm">
                  {t.descriptionLabel}
                </label>
                <EditorTextArea
                  form={form}
                  setForm={setForm}
                  disabled={loading}
                  en="description_en"
                  ar="description_ar"
                />
              </div>
              {/* Times */}
              <div className="flex items-center gap-4 flex-col sm:flex-row">
                <div className="flex flex-col gap-2 w-full">
                  <label htmlFor="duration" className="text-sm">
                    {t.durationLabel}
                  </label>
                  <TimePicker
                    className="w-full"
                    defaultValue={dayjs("01:00:00", "HH:mm:ss")}
                    format="HH:mm:ss"
                    onChange={handleDurationChange}
                    disabled={loading}
                  />
                  {errors?.duration && (
                    <p className="text-red-500 font-semibold text-sm">
                      {errors.duration[0]}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-2 w-full">
                  <label htmlFor="start_date" className="text-sm">
                    {t.dateRangeLabel}
                  </label>
                  <RangePicker
                    className="w-full"
                    showTime={{ format: "HH:mm" }}
                    format="YYYY-MM-DD HH:mm"
                    onChange={handleDateRangeChange}
                    disabled={loading}
                  />
                  {errors?.dates && (
                    <p className="text-red-500 font-semibold text-sm">
                      {errors.dates[0]}
                    </p>
                  )}
                </div>
              </div>
              {/* Show */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardContent className="p-4 space-y-4">
                    <div className="border-b pb-2">
                      <p className="font-bold text-xl">{t.showAnswer}</p>
                    </div>
                    <div className="grid gap-2">
                      <Select
                        value={form?.show_answer}
                        onValueChange={(e) =>
                          setForm({ ...form, show_answer: e })
                        }
                      >
                        <SelectTrigger
                          className="rounded-lg sm:ml-auto"
                          aria-label="Select status"
                        >
                          <SelectValue placeholder={t.selectAnswer} />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl">
                          <SelectItem value={1} className="rounded-lg">
                            {t.active}
                          </SelectItem>
                          <SelectItem value={0} className="rounded-lg">
                            {t.inactive}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {errors?.show_answer && (
                      <p className="text-red-500 font-semibold text-sm">
                        {errors.show_answer[0]}
                      </p>
                    )}
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 space-y-4">
                    <div className="border-b pb-2">
                      <p className="font-bold text-xl">{t.showDegree}</p>
                    </div>
                    <div className="grid gap-2">
                      <Select
                        value={form?.show_degree}
                        onValueChange={(e) =>
                          setForm({ ...form, show_degree: e })
                        }
                      >
                        <SelectTrigger
                          className="rounded-lg sm:ml-auto"
                          aria-label="Select status"
                        >
                          <SelectValue placeholder={t.selectDegree} />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl">
                          <SelectItem value={1} className="rounded-lg">
                            {t.active}
                          </SelectItem>
                          <SelectItem value={0} className="rounded-lg">
                            {t.inactive}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {errors?.show_degree && (
                      <p className="text-red-500 font-semibold text-sm">
                        {errors.show_degree[0]}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>
              {/* Select Ids */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {!!isTeacher && (
                  <>
                    {/* Lessons */}
                    <div className="flex flex-col gap-2 w-full">
                      <label htmlFor="due_date" className="text-sm">
                        {t.selectLesson}
                      </label>
                      <Select
                        value={form.lesson_id || ""}
                        onValueChange={(value) =>
                          setForm({
                            ...form,
                            lesson_id: +value,
                            category_id: "",
                            course_id: "",
                          })
                        }
                        className="w-full p-2 border rounded"
                        disabled={loadingLessons}
                      >
                        <SelectTrigger
                          className="rounded-lg"
                          aria-label={t.selectLesson}
                        >
                          <SelectValue placeholder={t.selectLesson} />
                        </SelectTrigger>
                        <SelectContent>
                          {lessons.length !== 0 ? (
                            lessons.map((lesson) => (
                              <SelectItem key={lesson} value={lesson.id}>
                                {lesson.title[language] ||
                                  lesson.title.en ||
                                  lesson.title.ar}
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem>No lessons found</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      {errors?.lesson_id && (
                        <p className="text-red-500 font-semibold text-sm">
                          {errors.lesson_id[0]}
                        </p>
                      )}
                    </div>
                    {/* Categories */}
                    {/* <div className="flex flex-col gap-2 w-full">
                      <label htmlFor="due_date" className="text-sm">
                        {t.selectCategory}
                      </label>
                      <Select
                        value={form.category_id || ""}
                        onValueChange={(value) =>
                          setForm({
                            ...form,
                            category_id: +value,
                            lesson_id: "",
                            course_id: "",
                          })
                        }
                        className="w-full p-2 border rounded"
                        disabled={loadingCategories}
                      >
                        <SelectTrigger
                          className="rounded-lg"
                          aria-label={t.selectCategory}
                        >
                          <SelectValue placeholder={t.selectCategory} />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat} value={cat.id}>
                              {cat.title[language] ||
                                cat.title.en ||
                                cat.title.ar}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors?.category_id && (
                        <p className="text-red-500 font-semibold text-sm">
                          {errors.category_id[0]}
                        </p>
                      )}
                    </div> */}
                    {/* Courses */}
                    {/* <div className="flex flex-col gap-2 w-full">
                      <label htmlFor="due_date" className="text-sm">
                        {t.selectCourse}
                      </label>
                      <Select
                        value={form.course_id || ""}
                        onValueChange={(value) =>
                          setForm({
                            ...form,
                            course_id: +value,
                            category_id: "",
                            lesson_id: "",
                          })
                        }
                        className="w-full p-2 border rounded"
                        disabled={loadingCourses}
                      >
                        <SelectTrigger
                          className="rounded-lg"
                          aria-label={t.selectCourse}
                        >
                          <SelectValue placeholder={t.selectCourse} />
                        </SelectTrigger>
                        <SelectContent>
                          {courses.map((course) => (
                            <SelectItem key={course} value={course.id}>
                              {course.title[language] ||
                                course.title.en ||
                                course.title.ar}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors?.course_id && (
                        <p className="text-red-500 font-semibold text-sm">
                          {errors.course_id[0]}
                        </p>
                      )}
                    </div> */}
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quiz Builder Section */}
      <div className="col-span-1 lg:col-span-2">
        <Card className="h-full flex flex-col">
          <CardContent className="p-4">
            <p className="font-bold text-xl border-b pb-2 mb-4">
              {language === "ar" ? "أسئلة الاختبار" : "Quiz Questions"}
            </p>
            <QuizBuilder
              initialQuestions={form.questions.map((q) => ({
                id: q.id,
                type:
                  q.type === "mcq"
                    ? QuestionType.MULTIPLE_CHOICE
                    : q.type === "true_false"
                    ? QuestionType.TRUE_FALSE
                    : q.type === "written"
                    ? QuestionType.PARAGRAPH
                    : QuestionType.FILE_UPLOAD,
                text: q.text || "",
                score: q.score,
                options: q.options.map((o) => ({
                  id: o.id,
                  text: o.text || "",
                  isCorrect: o.isCorrect === 1 || o.isCorrect === true,
                  image: o.image,
                })),
                imageUrl: q.file,
              }))}
              onQuestionsChange={updateQuestions}
              disabled={loading}
            />
            {errors?.questions && (
              <p className="text-red-500 font-semibold text-sm mt-2">
                {errors.questions[0]}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
